/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import { get as userGet } from 'js/lib/user';

import { Grid, InlineNotification, PageGridContainer, breakpoints } from '@coursera/cds-core';

import type { Product, ProductType } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import { EnterpriseTargetSkillProfileSummariesQueryV1 } from 'bundles/enterprise-legacy-learner-home/utils/ProgramHomeGraphqlQueries';
import type {
  ObjectiveSummaryQuery_EnterpriseTargetSkillProfileSummariesV1Resource_bySlug_elements as ObjectiveSummary,
  ObjectiveSummaryQuery as ObjectiveSummaryQueryData,
  ObjectiveSummaryQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/utils/__generated__/ObjectiveSummaryQuery';
import { getProductPath, launchMiniModal } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import SkillPageContent, {
  SkillPageContentPlaceholder,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/SkillPageContent';
import { isSkillIdEqual } from 'bundles/program-skills-objectives/utils';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/program-skills-objectives';

type TargetSkillProficiency = {
  skillId: string;
  skillName: string;
  targetProficiency: number;
  readableScore?: number;
};

type PropsFromRouter = {
  router: InjectedRouter;
  programSlug: string;
  objectiveSlug: string;
  skillId: string;
};

type PropsFromCaller = {
  programId: string;
  program: EnterpriseProgramsV1;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
};

type PropsFromGraphql = {
  objectiveSummary?: ObjectiveSummary | null;
  targetSkillProficiency?: TargetSkillProficiency | null;
  loading: boolean;
  error: boolean;
};

export type Props = PropsFromCaller & PropsFromRouter & PropsFromGraphql;

// This page displays course recommendations for a given skill within a skillset.
export const ProgramSkillSetSkillRecommendationsPage = ({
  objectiveSlug,
  programSlug,
  loading,
  error,
  objectiveSummary,
  targetSkillProficiency,
  thirdPartyOrganization,
  programId,
  program,
  router,
}: Props) => {
  const onProductCardClick = (product: Product, productType: ProductType, collectionId?: string | null) => {
    launchMiniModal(router, product.id, productType === 'course', collectionId || '', undefined, undefined, {
      slug: product.slug,
      productPagePath: getProductPath(product),
    });
  };

  const programName = program?.metadata?.name ?? _t('My Learning Program');
  const userId = userGet().id;

  const styles = {
    page: css`
      background-color: var(--cds-color-neutral-background-primary);
    `,
    container: css`
      margin: 0 auto;
    `,
    error: css`
      padding: var(--cds-spacing-300) var(--cds-spacing-600);
      ${breakpoints.down('sm')} {
        padding: var(--cds-spacing-300) var(--cds-spacing-150);
      }
    `,
    backNav: css`
      margin: var(--cds-spacing-300) 0 0 var(--cds-spacing-600);
      ${breakpoints.down('sm')} {
        margin-left: var(--cds-spacing-150);
      }
    `,
    back: css`
      margin-left: calc(var(--cds-spacing-100) * -1);
    `,
  };

  if (loading) {
    return (
      <main css={styles.container}>
        <PageGridContainer>
          <Grid item xs={12}>
            <SkillPageContentPlaceholder
              skillSetSlug={objectiveSlug}
              programSlug={programSlug}
              thirdPartyOrganization={thirdPartyOrganization}
              programName={programName}
            />
          </Grid>
        </PageGridContainer>
      </main>
    );
  }

  if (error) {
    return (
      <main css={[styles.container, styles.error]}>
        <InlineNotification severity="error">
          {_t('Sorry! Something went wrong. Please refresh the page.')}
        </InlineNotification>
      </main>
    );
  }

  if (!objectiveSummary || !targetSkillProficiency) {
    return null;
  }

  return (
    <main css={styles.container} className="rc-ProgramSkillSetSkillRecommendationsPage">
      <PageGridContainer>
        <Grid item xs={12}>
          <SkillPageContent
            programId={programId}
            skillId={targetSkillProficiency.skillId}
            targetScore={targetSkillProficiency.targetProficiency}
            onProductCardClick={onProductCardClick}
            userId={userId}
            skillSetSlug={objectiveSlug}
            skillSetName={objectiveSummary.title}
            programSlug={programSlug}
            thirdPartyOrganization={thirdPartyOrganization}
            programName={programName}
          />
        </Grid>
      </PageGridContainer>
    </main>
  );
};

const getTargetSkillProficiencyForSkillId = (
  objectiveSummary: ObjectiveSummary,
  skillId: string
): TargetSkillProficiency | null => {
  const targetSkillProficiency = objectiveSummary.targetSkillProficiencies.find((tsp) =>
    isSkillIdEqual(tsp.skillId, skillId)
  );

  return targetSkillProficiency
    ? {
        skillId,
        skillName: targetSkillProficiency.skillName,
        targetProficiency: targetSkillProficiency.targetProficiency,
      }
    : null;
};

const ProgramSkillSetSkillRecommendationsPageHookProxy = (props: Props) => (
  <ProgramSkillSetSkillRecommendationsPage {...props} />
);

export const enhance = compose<Props, PropsFromCaller>(
  connectToRouter<PropsFromRouter, {}>((router) => ({
    router,
    programSlug: router.params.programSlug,
    objectiveSlug: router.params.objectiveSlug,
    skillId: router.params.skillId,
  })),
  graphql<PropsFromRouter, ObjectiveSummaryQueryData, ObjectiveSummaryQueryVariables, PropsFromGraphql>(
    EnterpriseTargetSkillProfileSummariesQueryV1,
    {
      options: ({ objectiveSlug }) => ({
        variables: {
          objectiveSlug,
        },
      }),
      props: ({ data, ownProps }) => {
        const loading = data?.loading ?? true;
        const error = !!data?.error;
        const objectiveSummary = data?.EnterpriseTargetSkillProfileSummariesV1Resource?.bySlug?.elements[0];
        const targetSkillProficiency =
          !!ownProps?.skillId && objectiveSummary
            ? getTargetSkillProficiencyForSkillId(objectiveSummary, ownProps?.skillId)
            : null;
        return { loading, objectiveSummary, error, targetSkillProficiency };
      },
    }
  )
);

const PageWithHocs = enhance(ProgramSkillSetSkillRecommendationsPageHookProxy);

const ProgramSkillSetSkillRecommendationsPageRootContent = () => {
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();

  if (!program || !thirdPartyOrganization) {
    return null;
  }

  return (
    <PageWithHocs
      program={new EnterpriseProgramsV1(program)}
      programId={program?.id}
      thirdPartyOrganization={new ThirdPartyOrganizationsV1(thirdPartyOrganization)}
    />
  );
};

export default ProgramSkillSetSkillRecommendationsPageRootContent;
